import { createSignal, createEffect, onCleanup, onMount, For, Show, type Component } from "solid-js";
import type { ColumnDef } from "@tanstack/solid-table";
import { PanelLayout, type PanelStatus, type SubTab } from "../shared/panel-base";
import { DataTable } from "../shared/data-table";
import { useToolClient } from "../shared/tool-client";
import { useConfirm } from "../shared/confirm-dialog";
import { registerPanel } from "../panel-registry";

interface SessionRow {
  name: string;
  attached?: boolean | string | number;
  windows?: number | string;
  lastActivity?: string;
}

interface AiInfo {
  id: string;
  name?: string;
  installed?: boolean;
  version?: string;
  path?: string;
}

interface TmuxStatus {
  available?: boolean;
  version?: string;
  ais?: AiInfo[];
}

interface SessionListResult {
  rows?: SessionRow[];
  sessions?: SessionRow[];
}

interface CaptureResult {
  name?: string;
  content?: string;
  output?: string;
  lines?: string[];
}

interface AiCard {
  id: string;
  label: string;
}

const AI_CARDS: AiCard[] = [
  { id: "kimi", label: "Kimi" },
  { id: "claude-code", label: "Claude Code" },
  { id: "codex", label: "Codex" },
  { id: "opencode", label: "OpenCode" },
  { id: "hermes", label: "Hermes" },
];

const MANAGED_PREFIX = "rws-";
const SAFE_NAME_RE = /^[A-Za-z0-9._\-:]{1,128}$/;
const COMMAND_MAX_LEN = 4000;
const ANSI_RE = /\x1b\[[0-9;]*[a-zA-Z]/g;
const SESSION_POLL_MS = 3000;
const CAPTURE_POLL_MS = 2000;
const CAPTURE_LINES = 2000;

function isValidName(name: string): boolean {
  return SAFE_NAME_RE.test(name);
}

function isManaged(name: string): boolean {
  return name.startsWith(MANAGED_PREFIX);
}

function stripAnsi(text: string | undefined | null): string {
  return String(text == null ? "" : text).replace(ANSI_RE, "");
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "oui" : "non";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }
  return String(value);
}

const AiCliPanel: Component = () => {
  const tools = useToolClient();
  const confirmDialog = useConfirm();

  const [status, setStatus] = createSignal<PanelStatus>({ text: "", level: "info" });
  const [sessions, setSessions] = createSignal<SessionRow[]>([]);
  const [tmuxStatus, setTmuxStatus] = createSignal<TmuxStatus | null>(null);
  const [sessionFilter, setSessionFilter] = createSignal("");
  const [autoRefresh, setAutoRefresh] = createSignal(false);
  const [workDirs, setWorkDirs] = createSignal<Record<string, string>>({});
  const [argsMap, setArgsMap] = createSignal<Record<string, string>>({});

  // Capture overlay state
  const [captureName, setCaptureName] = createSignal<string | null>(null);
  const [captureContent, setCaptureContent] = createSignal("Chargement...");
  const [captureAuto, setCaptureAuto] = createSignal(false);
  const [captureCmd, setCaptureCmd] = createSignal("");

  function setErr(text: string): void {
    setStatus({ text, level: "danger" });
  }
  function setInfo(text: string): void {
    setStatus({ text, level: "info" });
  }
  function setWarn(text: string): void {
    setStatus({ text, level: "warn" });
  }

  async function fetchSessions(): Promise<void> {
    try {
      const result = await tools.call<SessionListResult>("cli_session_list", {});
      const rows = Array.isArray(result.rows) ? result.rows : Array.isArray(result.sessions) ? result.sessions : [];
      setSessions(rows);
      setInfo(`${rows.length} session(s)`);
    } catch (err) {
      setErr(`Erreur cli_session_list: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function fetchTmuxCheck(): Promise<void> {
    setInfo("Verification...");
    try {
      const result = await tools.call<TmuxStatus>("cli_check_tmux", {});
      setTmuxStatus(result);
      setStatus({
        text: result.available ? "tmux disponible." : "tmux indisponible.",
        level: result.available ? "info" : "warn",
      });
    } catch (err) {
      setErr(`Erreur cli_check_tmux: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // Session auto-refresh polling
  let sessionTimer: ReturnType<typeof setInterval> | null = null;
  createEffect(() => {
    if (sessionTimer) {
      clearInterval(sessionTimer);
      sessionTimer = null;
    }
    if (autoRefresh()) {
      sessionTimer = setInterval(() => {
        void fetchSessions();
      }, SESSION_POLL_MS);
    }
  });
  onCleanup(() => {
    if (sessionTimer) clearInterval(sessionTimer);
  });

  // Capture auto-refresh
  let captureTimer: ReturnType<typeof setInterval> | null = null;
  createEffect(() => {
    if (captureTimer) {
      clearInterval(captureTimer);
      captureTimer = null;
    }
    const name = captureName();
    if (name && captureAuto()) {
      captureTimer = setInterval(() => {
        void requestCapture(name);
      }, CAPTURE_POLL_MS);
    }
  });
  onCleanup(() => {
    if (captureTimer) clearInterval(captureTimer);
  });

  async function requestCapture(name: string): Promise<void> {
    if (!isValidName(name)) return;
    try {
      const result = await tools.call<CaptureResult>("cli_session_capture", {
        name,
        lines: CAPTURE_LINES,
      });
      const content =
        result.content != null
          ? result.content
          : result.output != null
            ? result.output
            : Array.isArray(result.lines)
              ? result.lines.join("\n")
              : "";
      setCaptureContent(stripAnsi(content));
    } catch (err) {
      setCaptureContent(`Erreur capture: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  function openCapture(name: string): void {
    if (!isValidName(name)) {
      setErr("Nom de session invalide.");
      return;
    }
    setCaptureName(name);
    setCaptureContent("Chargement de la capture...");
    setCaptureCmd("");
    setCaptureAuto(false);
    void requestCapture(name);
  }

  function closeCapture(): void {
    setCaptureName(null);
    setCaptureAuto(false);
    setCaptureContent("");
  }

  async function triggerSessionSend(name: string, command: string): Promise<void> {
    if (!isValidName(name)) {
      setErr("Nom de session invalide.");
      return;
    }
    const cmd = String(command || "");
    if (!cmd.trim()) {
      setWarn("Commande vide.");
      return;
    }
    if (cmd.length > COMMAND_MAX_LEN) {
      setErr(`Commande trop longue (>${COMMAND_MAX_LEN}).`);
      return;
    }
    const preview = cmd.length > 200 ? cmd.slice(0, 200) + "..." : cmd;
    const ok = await confirmDialog.confirm({
      title: `Envoyer commande - ${name}`,
      body: `Envoyer la commande suivante dans la session ${name} ?\n\n${preview}`,
      level: "warn",
      confirmLabel: "Envoyer",
      entityName: name,
    });
    if (!ok) return;
    setInfo(`Envoi commande vers ${name}...`);
    try {
      await tools.call("cli_session_send", { name, command: cmd }, { userConfirmedAt: new Date().toISOString() });
      setInfo(`Commande envoyee a ${name}.`);
      if (captureName() === name) void requestCapture(name);
    } catch (err) {
      setErr(`Erreur cli_session_send: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function triggerSessionAction(name: string, action: "capture" | "send" | "restart" | "kill"): Promise<void> {
    if (!isValidName(name)) {
      setErr("Nom de session invalide.");
      return;
    }
    if (action === "capture") {
      openCapture(name);
      return;
    }
    if (action === "send") {
      const cmd = window.prompt(`Commande a envoyer dans ${name} :`, "");
      if (cmd == null) return;
      await triggerSessionSend(name, cmd);
      return;
    }
    if (!isManaged(name)) {
      setWarn(`Action ${action} refusee : session non manageee.`);
      return;
    }
    const label = action === "restart" ? "Redemarrer" : "Tuer";
    const ok = await confirmDialog.confirm({
      title: `${label} session - ${name}`,
      body: `Confirmer l'action "${label}" sur la session tmux ${name} ?`,
      level: "danger",
      confirmLabel: label,
      entityName: name,
    });
    if (!ok) return;
    const tool = action === "restart" ? "cli_session_restart" : "cli_session_kill";
    setInfo(`${label} ${name}...`);
    try {
      await tools.call(tool, { name }, { userConfirmedAt: new Date().toISOString() });
      setInfo(`Session ${name} ${action === "restart" ? "redemarree" : "tuee"}.`);
      if (action === "kill" && captureName() === name) closeCapture();
      void fetchSessions();
    } catch (err) {
      setErr(`Erreur ${tool}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function triggerAiStart(aiId: string): Promise<void> {
    const card = AI_CARDS.find((c) => c.id === aiId);
    if (!card) return;
    const workDir = (workDirs()[aiId] || "").trim();
    const argsRaw = (argsMap()[aiId] || "").trim();
    if (argsRaw.length > COMMAND_MAX_LEN) {
      setErr(`Args trop longs (>${COMMAND_MAX_LEN}).`);
      return;
    }
    const ok = await confirmDialog.confirm({
      title: `Demarrer ${card.label}`,
      body: `Lancer ${card.label}${workDir ? ` dans ${workDir}` : ""} dans une nouvelle session tmux manageee ?`,
      level: "warn",
      confirmLabel: "Demarrer",
      entityName: aiId,
    });
    if (!ok) return;
    const params: Record<string, unknown> = { ai: aiId };
    if (workDir) params.workDir = workDir;
    if (argsRaw) params.args = argsRaw;
    setInfo(`Demarrage ${card.label}...`);
    try {
      await tools.call("cli_ai_start", params, { userConfirmedAt: new Date().toISOString() });
      setInfo(`AI ${card.label} demarree.`);
      void fetchSessions();
    } catch (err) {
      setErr(`Erreur cli_ai_start: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // Initial mount : pre-load tmux status for AI cards installed badges.
  onMount(() => {
    void fetchTmuxCheck();
    void fetchSessions();
  });

  const aiInstalledMap = (): Record<string, AiInfo | undefined> => {
    const map: Record<string, AiInfo | undefined> = {};
    const ais = tmuxStatus()?.ais || [];
    for (const a of ais) map[String(a.id || a.name || "")] = a;
    return map;
  };

  const filteredSessions = (): SessionRow[] => {
    const f = sessionFilter().toLowerCase();
    if (!f) return sessions();
    return sessions().filter((r) =>
      String(r.name || "")
        .toLowerCase()
        .includes(f),
    );
  };

  const sessionColumns: ColumnDef<SessionRow>[] = [
    { header: "name", accessorKey: "name" },
    {
      header: "managed",
      id: "managed",
      accessorFn: (row) => (isManaged(String(row.name || "")) ? "managed" : "externe"),
      cell: (info) => {
        const managed = isManaged(String(info.row.original.name || ""));
        return <span class={`badge ${managed ? "badge--ok" : "badge--warn"}`}>{managed ? "managed" : "externe"}</span>;
      },
    },
    { header: "attached", accessorKey: "attached", cell: (info) => formatCell(info.getValue()) },
    { header: "windows", accessorKey: "windows", cell: (info) => formatCell(info.getValue()) },
    {
      header: "lastActivity",
      accessorKey: "lastActivity",
      cell: (info) => formatCell(info.getValue()),
    },
  ];

  const sessionsTab: SubTab = {
    id: "sessions",
    label: "Sessions",
    render: () => (
      <>
        <div class="panel-toolbar panel-toolbar--filter">
          <input
            type="text"
            class="panel-filter-input"
            placeholder="Filtre nom..."
            spellcheck={false}
            value={sessionFilter()}
            onInput={(e) => setSessionFilter(e.currentTarget.value)}
          />
          <button type="button" class="btn btn--xs btn--info" onClick={() => void fetchSessions()}>
            Actualiser
          </button>
          <label class="panel-filter-checkbox">
            <input type="checkbox" checked={autoRefresh()} onChange={(e) => setAutoRefresh(e.currentTarget.checked)} />
            <span> Auto-refresh 3s</span>
          </label>
        </div>
        <DataTable
          rows={filteredSessions()}
          columns={sessionColumns}
          emptyMessage="Aucune session tmux."
          rowActions={(row) => {
            const managed = isManaged(String(row.name || ""));
            return (
              <>
                <button
                  type="button"
                  class="btn btn--xs btn--info"
                  onClick={() => void triggerSessionAction(row.name, "capture")}
                >
                  Capturer
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--warn"
                  onClick={() => void triggerSessionAction(row.name, "send")}
                >
                  Envoyer commande
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--danger"
                  disabled={!managed}
                  onClick={() => void triggerSessionAction(row.name, "restart")}
                >
                  Restart
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--danger"
                  disabled={!managed}
                  onClick={() => void triggerSessionAction(row.name, "kill")}
                >
                  Kill
                </button>
              </>
            );
          }}
        />
      </>
    ),
  };

  const launchTab: SubTab = {
    id: "launch",
    label: "Lancer AI",
    render: () => (
      <div
        style={{
          display: "grid",
          "grid-template-columns": "repeat(2, minmax(0, 1fr))",
          gap: "12px",
        }}
      >
        <For each={AI_CARDS}>
          {(card) => {
            const info = aiInstalledMap()[card.id];
            const installed = info ? Boolean(info.installed) : null;
            return (
              <div
                class="panel-section"
                style={{
                  padding: "10px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  "border-radius": "8px",
                }}
              >
                <div class="panel-section-header">
                  <span class="eyebrow">{card.label}</span>
                  <span class="badge" data-level={installed == null ? "" : installed ? "ok" : "warn"}>
                    {installed == null ? "?" : installed ? "installe" : "absent"}
                  </span>
                </div>
                <label style={{ display: "block", "font-size": "12px", "margin-top": "8px" }}>workDir</label>
                <input
                  type="text"
                  class="panel-filter-input"
                  placeholder="$HOME"
                  spellcheck={false}
                  value={workDirs()[card.id] || ""}
                  onInput={(e) => setWorkDirs((prev) => ({ ...prev, [card.id]: e.currentTarget.value }))}
                />
                <label style={{ display: "block", "font-size": "12px", "margin-top": "8px" }}>args (optionnel)</label>
                <textarea
                  class="panel-filter-input"
                  rows={2}
                  spellcheck={false}
                  value={argsMap()[card.id] || ""}
                  onInput={(e) => setArgsMap((prev) => ({ ...prev, [card.id]: e.currentTarget.value }))}
                />
                <div class="panel-row-actions" style={{ "margin-top": "10px" }}>
                  <button type="button" class="btn btn--xs btn--warn" onClick={() => void triggerAiStart(card.id)}>
                    Demarrer
                  </button>
                </div>
              </div>
            );
          }}
        </For>
      </div>
    ),
  };

  const diagColumns: ColumnDef<{ name: string; installed: boolean; detail: string }>[] = [
    { header: "Composant", accessorKey: "name" },
    {
      header: "Etat",
      id: "etat",
      accessorFn: (row) => (row.installed ? "ok" : "absent"),
      cell: (info) => (
        <span class={`badge ${info.row.original.installed ? "badge--ok" : "badge--warn"}`}>
          {info.row.original.installed ? "ok" : "absent"}
        </span>
      ),
    },
    { header: "Detail", accessorKey: "detail" },
  ];

  const diagRows = (): { name: string; installed: boolean; detail: string }[] => {
    const t = tmuxStatus();
    if (!t) return [];
    const rows: { name: string; installed: boolean; detail: string }[] = [
      { name: "tmux", installed: Boolean(t.available), detail: String(t.version || "") },
    ];
    for (const ai of t.ais || []) {
      const known = AI_CARDS.find((c) => c.id === ai.id);
      rows.push({
        name: known ? known.label : String(ai.id || ai.name || ""),
        installed: Boolean(ai.installed),
        detail: String(ai.path || ai.version || ""),
      });
    }
    return rows;
  };

  const diagnosticTab: SubTab = {
    id: "diagnostic",
    label: "Diagnostic",
    render: () => (
      <>
        <div class="panel-toolbar panel-toolbar--filter">
          <button type="button" class="btn btn--xs btn--info" onClick={() => void fetchTmuxCheck()}>
            Verifier tmux + AIs
          </button>
        </div>
        <DataTable rows={diagRows()} columns={diagColumns} emptyMessage="Cliquez sur Verifier pour charger." />
      </>
    ),
  };

  return (
    <>
      <PanelLayout title="AI CLI" subtabs={[sessionsTab, launchTab, diagnosticTab]} status={status()} />
      <Show when={captureName()}>
        {(name) => (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              inset: "0",
              "z-index": "8000",
              background: "rgba(0,0,0,0.78)",
              display: "flex",
              "flex-direction": "column",
              padding: "24px",
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") closeCapture();
            }}
          >
            <div class="panel-section-header" style={{ color: "#e6eaf2" }}>
              <span class="eyebrow">Capture - {name()}</span>
              <div class="panel-row-actions">
                <button type="button" class="btn btn--xs btn--info" onClick={() => void requestCapture(name())}>
                  Recharger
                </button>
                <label class="panel-filter-checkbox" style={{ color: "#e6eaf2" }}>
                  <input
                    type="checkbox"
                    checked={captureAuto()}
                    onChange={(e) => setCaptureAuto(e.currentTarget.checked)}
                  />
                  <span> Auto-refresh 2s</span>
                </label>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => closeCapture()}>
                  Fermer
                </button>
              </div>
            </div>
            <pre
              style={{
                flex: "1 1 auto",
                overflow: "auto",
                margin: "12px 0",
                padding: "12px",
                background: "#0a0d14",
                color: "#d6dae3",
                "font-family": "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                "font-size": "12.5px",
                "white-space": "pre-wrap",
                "word-break": "break-all",
                border: "1px solid rgba(255,255,255,0.08)",
                "border-radius": "6px",
              }}
            >
              {captureContent()}
            </pre>
            <div class="panel-toolbar panel-toolbar--filter">
              <input
                type="text"
                class="panel-filter-input"
                placeholder="Commande a envoyer (Enter valide)"
                maxLength={COMMAND_MAX_LEN}
                spellcheck={false}
                value={captureCmd()}
                onInput={(e) => setCaptureCmd(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void triggerSessionSend(name(), captureCmd());
                  }
                }}
              />
              <button
                type="button"
                class="btn btn--xs btn--warn"
                onClick={() => void triggerSessionSend(name(), captureCmd())}
              >
                Envoyer
              </button>
            </div>
          </div>
        )}
      </Show>
    </>
  );
};

registerPanel("aicli", AiCliPanel);

export default AiCliPanel;
